import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import themeService from '../../services/themeService';
import { Theme, ThemeMode } from '../../types';

interface ThemeContextType {
    theme: Theme;
    themeMode: ThemeMode;
    setTheme: (mode: ThemeMode) => void;
    isDarkMode: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
    children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [theme, setThemeState] = useState<Theme>(themeService.getTheme());
    const [themeMode, setThemeModeState] = useState<ThemeMode>(themeService.getThemeMode());

    useEffect(() => {
        // Subscribe to theme changes
        const unsubscribe = themeService.onThemeChange((newTheme) => {
            setThemeState(newTheme);
            setThemeModeState(themeService.getThemeMode());
        });

        return unsubscribe;
    }, []);

    const setTheme = (mode: ThemeMode) => {
        themeService.setTheme(mode);
    };

    const value: ThemeContextType = {
        theme,
        themeMode,
        setTheme,
        isDarkMode: themeService.isDarkMode()
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
